import Logger from "../../clients/sentry";
import { ValidationError, handleError } from "../../errors";
import { getEstimateReportData, getProjectReportData } from "../../excel/workbook";
import { budgetImportFor, hasBudgetImport } from "../../integrations/exports/registry";
import { createJoinExport as buildJoinExport } from "../../integrations/exports/join";
import { ProcoreExportOptions, createProcoreExport as buildProcoreExport } from "../../integrations/exports/procore";
import { getTenant } from "../../utils/tenant";

/** Hand the user a file. */
function createDownload(data: string, filename: string, type: string) {
  const blob = new Blob([data], { type: `${type}` });

  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;

  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}

const sanitizeForFileName = (value: unknown): string => String(value ?? "").replace(/[<>:"/\\|?*\s]/g, "-");

const buildExportFileName = (projectName: unknown, estimateName: unknown, extension: string): string =>
  `${sanitizeForFileName(projectName)}_${sanitizeForFileName(estimateName)}_${new Date()
    .toISOString()
    .replace(/[-:.Z]/g, "")
    .slice(0, -3)}.${extension}`;

/**
 * The three file exports on User Settings: a tenant's budget import, a Join import and a Procore
 * import. Each reads what the workbook currently holds, asks `integrations/exports/` to build the
 * rows, and hands the result down as a file.
 *
 * Every failure path throws. An earlier version returned a falsy result for "no tenant", "not
 * configured" and "no rows" alike, which the caller rendered as nothing happening at all.
 */
export function useExportActions() {
  const createBudgetImport = async (): Promise<void> => {
    try {
      const estimateReport = await getEstimateReportData();
      const projectReport = await getProjectReportData();
      const tenant = getTenant();

      if (!estimateReport?.[0]?.estimate_id) {
        throw new ValidationError("Load an estimate before creating a budget import.");
      }
      if (!hasBudgetImport(tenant)) {
        throw new ValidationError(
          tenant
            ? `Budget import isn't set up for the "${tenant}" tenant.`
            : "No tenant is loaded, so there is no budget import format to use."
        );
      }

      const [budgetData, extension] = await budgetImportFor(tenant)();
      if (budgetData.length === 0) {
        throw new ValidationError("The budget import came back empty — check the estimate has line items.");
      }

      const fileName = buildExportFileName(projectReport[0]?.project_name, estimateReport[0]?.estimate_name, extension);
      createDownload(budgetData.join("\n"), fileName, "text/plain");
    } catch (error) {
      const err = handleError(error, { context: "createBudgetImport" });
      Logger.error("Failed to create budget import", err);
      throw err;
    }
  };

  const createJoinExport = async (): Promise<void> => {
    try {
      const estimateReport = await getEstimateReportData();
      const projectReport = await getProjectReportData();

      if (!estimateReport?.[0]?.estimate_id) {
        throw new ValidationError("Load an estimate before creating a Join import file.");
      }

      const [data, extension] = await buildJoinExport();
      if (data.length === 0) {
        throw new ValidationError("The Join export came back empty — check the estimate has line items.");
      }

      const fileName = buildExportFileName(projectReport[0]?.project_name, estimateReport[0]?.estimate_name, extension);
      createDownload(data.join("\n"), fileName, "text/plain");
    } catch (error) {
      const err = handleError(error, { context: "createJoinExport" });
      Logger.error("Failed to create Join import file", err);
      throw err;
    }
  };

  const createProcoreExport = async (opts: ProcoreExportOptions): Promise<void> => {
    try {
      const estimateReport = await getEstimateReportData();
      const projectReport = await getProjectReportData();
      // Guard before running the export so procore.ts never calls getProcessedEstimate(undefined)
      // (a spurious estimates/undefined/... request + confusing error) when no estimate is loaded.
      if (!estimateReport[0]?.estimate_id) {
        throw new ValidationError("Load an estimate before creating a Procore export.");
      }
      const [data, extension] = await buildProcoreExport(opts);
      if (data.length === 0) {
        throw new ValidationError("The Procore export came back empty — check the estimate has line items.");
      }
      const fileName = buildExportFileName(projectReport[0]?.project_name, estimateReport[0]?.estimate_name, extension);
      createDownload(data.join("\n"), fileName, "text/plain");
    } catch (error) {
      const err = handleError(error, { context: "createProcoreExport" });
      Logger.error("Failed to create Procore import file", err);
      throw err;
    }
  };

  return { createBudgetImport, createJoinExport, createProcoreExport };
}
